import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { useAppSelector } from '../../../app/hooks'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../app/store'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  GET_OPD_SYSTEMIC_ENDPOINT,
  POST_OPD_SYSTEMIC_ENDPOINT,
} from '../../../utils/Endpoints'
import { getVisitType, SYSTEMIC_TEXT_FIELDS, toastMessages } from './constants'
import {
  isInitialLoadForOPDForms,
  isSystemicExamOpdFormSubmitted,
  selectSystemicExamination,
  setIsSystemicExamOpdFormSubmit,
  setLoading,
  updateSystemicExamination,
} from './opdFormsSlice'
import {
  opdFormDataFetchApi,
  opdFormDataSaveApi,
} from '../../../services/OpdFormsSTTService'

const styles = (theme: Theme) => ({
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      width: '250px',
      padding: '4px',
    },
  },
  fieldLabel: {
    textAlign: 'right',
    minWidth: '250px',
    textWrap: 'wrap',
    fontSize: '12px',
    [theme.breakpoints.between('sm', 'md')]: {
      minWidth: '147px',
    },
    [theme.breakpoints.up('md')]: {
      minWidth: '220px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '70%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '65%',
    },
  },
})

const Systemic = (intentType: any) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const { id } = useParams()
  const visitType = getVisitType(intentType.intent)
  const dispatch = useDispatch<AppDispatch>()
  const systemicExaminationData: any = useAppSelector(selectSystemicExamination)
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isSystemicExamOpdFormSubmitted)

  const initialValues = {
    localExamination: systemicExaminationData?.localExamination || '',
    cardiovascularSystem: systemicExaminationData?.cardiovascularSystem || '',
    respiratorySystem: systemicExaminationData?.respiratorySystem || '',
    nervousSystem: systemicExaminationData?.nervousSystem || '',
    perAbdomen: systemicExaminationData?.perAbdomen || '',
  }

  const validationSchema = Yup?.object({
    localExamination: Yup?.string(),
    cardiovascularSystem: Yup?.string(),
    respiratorySystem: Yup?.string(),
    nervousSystem: Yup?.string(),
    perAbdomen: Yup?.string(),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      handleSystemicSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleSystemicSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...data,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_SYSTEMIC_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsSystemicExamOpdFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }
    setSubmitting(false)
  }

  const getSystemic = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getSystemicUrl = `${GET_OPD_SYSTEMIC_ENDPOINT}${id}&visitType=opd/${visitType}`

    const response = await opdFormDataFetchApi(getSystemicUrl)

    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateSystemicExamination({
          localExamination: result?.localExamination || '',
          cardiovascularSystem: result?.cvs || '',
          respiratorySystem: result?.respsys || '',
          nervousSystem: result?.nervsys || '',
          perAbdomen: result?.perabd || '',
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }

    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getSystemic()
  }, [getSystemic])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      gap={1}
      width={'fit-content'}
      className={classes?.customFontBox}
    >
      {SYSTEMIC_TEXT_FIELDS?.map(({ name, label }) => (
        <Box
          display={'flex'}
          justifyContent={'start'}
          alignItems={'center'}
          width={'100%'}
          key={name}
          flexWrap={'wrap'}
          gap={2}
        >
          <Typography className={classes?.fieldLabel}>{label}:</Typography>

          <TextField
            inputProps={{ 'data-testid': name }}
            size='small'
            multiline
            rows={3}
            variant='outlined'
            name={name}
            value={formik?.values[name as keyof typeof formik.values] || ''}
            onChange={formik?.handleChange}
            onBlur={formik?.handleBlur}
            error={
              formik?.touched[name as keyof typeof formik.values] &&
              Boolean(formik?.errors[name as keyof typeof formik.errors])
            }
            className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
          />
        </Box>
      ))}

      <Box
        display={'flex'}
        justifyContent={'flex-end'}
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
          className={classes?.submitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default Systemic
